// Thin fetch wrapper. Auth rides on the same signed session cookie the Rails
// pages use, so every mutating request carries the CSRF token Rails put in the
// page head.

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

class ApiError extends Error {
  // The whole body is kept, not just the message. A 402 carries the meter with
  // it — how many free evaluations were used, out of how many — so the paywall
  // can render without a second round trip, and so the client never has to
  // hardcode a limit the server might be running an experiment on.
  constructor(message, status, payload) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = csrfToken()
  if (token) headers['X-CSRF-Token'] = token

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 204) return null

  const text = await response.text()
  const payload = text ? JSON.parse(text) : null

  if (!response.ok) {
    throw new ApiError(
      payload?.error || `Request failed (${response.status})`,
      response.status,
      payload,
    )
  }

  return payload
}

export const api = {
  session: () => request('/session'),
  signIn: (email_address, password) =>
    request('/session', { method: 'POST', body: { email_address, password } }),
  signUp: (email_address, password) =>
    request('/registration', { method: 'POST', body: { email_address, password } }),
  signOut: () => request('/session', { method: 'DELETE' }),

  // The current password is required by the server, not just by the form: a
  // stolen session must not be convertible into permanent ownership of the
  // account. Succeeding here ends every *other* session.
  changePassword: (current_password, password) =>
    request('/password', { method: 'PATCH', body: { current_password, password } }),

  // Another copy of the sign-up link. Rate limited per user, and refused
  // outright once the address is confirmed.
  resendConfirmation: () => request('/email_confirmation', { method: 'POST' }),

  platformAccounts: () => request('/platform_accounts'),
  // Doubles as "change my Sleeper username": the server resolves the name to
  // Sleeper's stable user id, so re-linking the same account under a new
  // display name updates the row rather than adding one.
  linkSleeper: (username) =>
    request('/platform_accounts', { method: 'POST', body: { username } }),
  // ESPN has no OAuth, so the credential is the user's own session cookies.
  // Sent once and never read back — the API does not echo them.
  linkEspn: ({ espn_s2, swid }) =>
    request('/platform_accounts', { method: 'POST', body: { provider: 'espn', espn_s2, swid } }),
  unlinkPlatformAccount: (id) => request(`/platform_accounts/${id}`, { method: 'DELETE' }),
  availableLeagues: (accountId) => request(`/platform_accounts/${accountId}/leagues`),

  leagues: () => request('/leagues'),
  league: (id) => request(`/leagues/${id}`),
  connectLeague: (platform_account_id, external_id) =>
    request('/leagues/connect', { method: 'POST', body: { platform_account_id, external_id } }),
  // No account at all: a provider that serves public leagues reads one
  // anonymously, and a private one answers 401 with the reason to link.
  importPublicLeague: (provider, external_id) =>
    request('/leagues/connect', { method: 'POST', body: { provider, external_id } }),
  // Which team is yours, for an import that could not tell. null clears it.
  setMyTeam: (leagueId, fantasy_team_id) =>
    request(`/leagues/${leagueId}/connection`, { method: 'PATCH', body: { fantasy_team_id } }),
  syncLeague: (id) => request(`/leagues/${id}/sync`, { method: 'POST' }),

  leaguePlayers: (leagueId, { teamId, q } = {}) => {
    const params = new URLSearchParams()
    if (teamId) params.set('team_id', teamId)
    if (q) params.set('q', q)
    const query = params.toString()
    return request(`/leagues/${leagueId}/players${query ? `?${query}` : ''}`)
  },

  // Estimates, not evaluations: the server never persists a trade for these,
  // so browsing them costs nothing against the meter. Opening one goes through
  // createTrade below and is metered like any other evaluation.
  leagueSuggestions: (leagueId, { teamId } = {}) => {
    const params = new URLSearchParams()
    if (teamId) params.set('team_id', teamId)
    const query = params.toString()
    return request(`/leagues/${leagueId}/suggestions${query ? `?${query}` : ''}`)
  },

  createTrade: (league_id, assets, { exploratory } = {}) =>
    request('/trades', { method: 'POST', body: { league_id, assets, exploratory } }),
  trade: (id) => request(`/trades/${id}`),
  evaluation: (id) => request(`/trades/${id}/evaluation`),

  trades: () => request('/trades'),
  saveTrade: (id, name) => request(`/trades/${id}`, { method: 'PATCH', body: { name } }),
  deleteTrade: (id) => request(`/trades/${id}`, { method: 'DELETE' }),

  shareTrade: (id) => request(`/trades/${id}/share`, { method: 'POST' }),
  unshareTrade: (id) => request(`/trades/${id}/share`, { method: 'DELETE' }),
  sharedTrade: (token) => request(`/shared/${token}`),

  entitlement: () => request('/billing/entitlement'),
  price: () => request('/billing/price'),
  // renewal_consent is not a formality: California's automatic renewal law
  // wants consent to the renewal terms as its own step, and the server refuses
  // without it.
  startCheckout: (renewal_consent) =>
    request('/billing/checkout', { method: 'POST', body: { renewal_consent } }),
  openPortal: () => request('/billing/portal', { method: 'POST' }),

  // Billing state, as opposed to access. Read even when the caller has no
  // access at all, because a paused subscriber part-way through their season
  // away has none and is still going to be charged — they are exactly the
  // person who needs the account page to say something.
  subscription: () => request('/billing/subscription'),
  // Skip one season. Access runs out at the end of the term already paid for,
  // exactly as cancelling would; the difference is that it comes back on its
  // own the season after.
  pauseSubscription: () => request('/billing/subscription/pause', { method: 'POST' }),
  // Undoes either of the two below/above — the server works out which was
  // asked for. See the controller: the distinction is not one a person should
  // have to hold in their head.
  resumeSubscription: () => request('/billing/subscription/resume', { method: 'POST' }),
  // Stops the next charge. Does not end the term already paid for, and does
  // not delete anything, which is why the account page can offer Resume
  // straight afterwards.
  cancelSubscription: () => request('/billing/subscription', { method: 'DELETE' }),

  // Admin. Cookie-only on the server: these are the one part of the API that
  // refuses a bearer token, and they answer 404 rather than 403 to a non-admin,
  // so a failure here is not evidence the route exists.
  adminUsers: (q) => request(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  adminUser: (id) => request(`/admin/users/${id}`),
  adminSetFreeLimit: (id, free_evaluation_limit_override, reason) =>
    request(`/admin/users/${id}`, {
      method: 'PATCH',
      body: { free_evaluation_limit_override, reason },
    }),
  adminGrantComp: (id, { mode, months, reason }) =>
    request(`/admin/users/${id}/comp`, { method: 'POST', body: { mode, months, reason } }),
  adminRevokeComp: (id, reason) =>
    request(`/admin/users/${id}/comp`, { method: 'DELETE', body: { reason } }),
  adminSendPasswordReset: (id, reason) =>
    request(`/admin/users/${id}/password_reset`, { method: 'POST', body: { reason } }),
  // confirm_email is the confirmation, not a dialog: a stray click on a list
  // row must not be able to destroy an account.
  adminDeleteUser: (id, confirm_email, reason) =>
    request(`/admin/users/${id}`, { method: 'DELETE', body: { confirm_email, reason } }),
}

export { ApiError }
